import React, { useEffect, useState } from "react";
import { Plus, SkipForward } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/shownotes/NoteSection";
import SceneRow from "@/components/shownotes/SceneRow";
import { blankScene, fmtClock, parseClock, sumSeconds, sceneKey, liveSeconds } from "@/components/shownotes/meta";

const commit = (scenes, timer) => (timer ? scenes.map((s) => (s.key === timer.key ? { ...s, time: fmtClock(liveSeconds(timer)) } : s)) : scenes);

// Re-render while a stopwatch runs and warn before leaving the page.
function useRunning(timer) {
  const [, tick] = useState(0);
  useEffect(() => {
    if (!timer) return undefined;
    const id = setInterval(() => tick((t) => t + 1), 250);
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => { clearInterval(id); window.removeEventListener("beforeunload", warn); };
  }, [timer]);
}

// Rehearsal Notes Scene Run: rows of name + MM:SS, one live stopwatch at a time, "Next scene" to keep timing.
export default function SceneRuns({ scenes, timer, names = [], set }) {
  useRunning(timer);
  const setRow = (key, patch) => set({ scenes: scenes.map((s) => (s.key === key ? { ...s, ...patch } : s)) });
  const toggle = (s) => (timer?.key === s.key
    ? set({ scenes: commit(scenes, timer), timer: null })
    : set({ scenes: commit(scenes, timer), timer: { key: s.key, start: Date.now(), base: parseClock(s.time) || 0 } }));
  const next = () => {
    const row = blankScene();
    set({ scenes: [...commit(scenes, timer), row], timer: { key: row.key, start: Date.now(), base: 0 } });
  };
  const remove = (key) => set({ scenes: scenes.filter((s) => s.key !== key), ...(timer?.key === key ? { timer: null } : {}) });
  const optionsFor = (key) => {
    const taken = new Set(scenes.filter((s) => s.key !== key).map((s) => sceneKey(s.scene)));
    return names.filter((n) => !taken.has(sceneKey(n)));
  };
  const secs = (s) => (timer?.key === s.key ? liveSeconds(timer) : parseClock(s.time));
  const total = sumSeconds(scenes.map(secs));
  return (
    <Field label="Scene Run">
      <div className="space-y-2" data-testid="notes-scenes">
        {scenes.map((s, i) => (
          <SceneRow key={s.key} s={s} i={i} options={optionsFor(s.key)} live={timer?.key === s.key ? liveSeconds(timer) : null}
            setRow={(p) => setRow(s.key, p)} onToggle={() => toggle(s)} onRemove={() => remove(s.key)} />
        ))}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={() => set({ scenes: [...scenes, blankScene()] })} data-testid="notes-add-scene-btn">
              <Plus className="mr-1 h-3.5 w-3.5" /> Add Another Scene Run Time
            </Button>
            {timer && (
              <Button type="button" size="sm" className="rounded-full bg-rose-600 hover:bg-rose-700 animate-fade-in-up" onClick={next} data-testid="notes-scene-next-btn">
                <SkipForward className="mr-1 h-3.5 w-3.5" /> Next scene
              </Button>
            )}
          </div>
          <p className="text-xs font-semibold text-slate-600" data-testid="notes-scenes-total">Total scene time: <span className="tabular-nums text-slate-900">{total ? fmtClock(total) : "—"}</span></p>
        </div>
      </div>
    </Field>
  );
}
